import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, ConfigProvider, Menu } from 'antd';
import { useSidebarSiteQuery } from '@/.generated/graphql';
import { Icon } from '../primitives/icon/Icon';
import { SearchInput } from '../primitives/SearchInput';
import { updateUrlParams } from './utils';
import type { NavigateFunction } from 'react-router-dom';
import { MenuFoldOutIcon } from '../Icons';
import { TextMatchDisplay } from '@/components/primitives/TextMatchDisplay';
import { BuildingEditMenu } from './BuildingEditMenu';
import { SiteEditMenu } from './SiteEditMenu';
import { datadogRum } from '@datadog/browser-rum';
import { gql } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { getTypeFromId } from '../studio/utils/utils';
import { useFeatureGate } from '@statsig/react-bindings';
import { CreateDropdownButton } from './CreateDropdownButton';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';
import { useHiddenSpaces } from './useHiddenSpaces';

interface SidebarMenuProps {
  onSidebarOpenClose: (close: boolean) => void;
  sidebarOpen: boolean;
  search: string;
  pathname: string;
  navigate: NavigateFunction;
}
type MenuItem = {
  key: string;
  label: string | React.ReactNode;
  type?: any;
  children?: MenuItem[];
};

// Checkbox primary color tracks the theme's text-primary token so the hide
// toggle adapts to light/dark without a separate dark override.
const sidebarCheckboxTheme = {
  components: {
    Checkbox: {
      colorPrimary: 'var(--color-text-primary)',
      colorPrimaryHover: 'var(--color-text-primary)',
      colorPrimaryBorder: 'var(--color-text-primary)',
    },
  },
};

// Prevents checkbox clicks from triggering menu expand/collapse
function HideCheckbox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
      <ConfigProvider theme={sidebarCheckboxTheme}>
        <Checkbox checked={checked} onChange={onChange} className="mr-2 flex-shrink-0" />
      </ConfigProvider>
    </div>
  );
}

/** Collect a node and all its descendant IDs for cascade hide/unhide */
export function collectDescendantIds(node: {
  id: string;
  buildings?: { id: string; floors?: { id: string }[] }[];
  floors?: { id: string }[];
}): string[] {
  if ('buildings' in node && node.buildings) {
    return [
      node.id,
      ...node.buildings.flatMap((b) => [b.id, ...(b.floors?.map((f) => f.id) || [])]),
    ];
  }
  if ('floors' in node && node.floors) {
    return [node.id, ...node.floors.map((f) => f.id)];
  }
  return [node.id];
}

const MENU_STYLE = { borderRight: '0px' } as const;

const naturalSort = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });

const SidebarMenu: React.FC<SidebarMenuProps> = ({
  onSidebarOpenClose,
  sidebarOpen,
  search,
  pathname,
  navigate,
}) => {
  const { data: rawSiteData, error: siteError } = useSidebarSiteQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Stabilize site data reference. When the user switches tabs (Studio→Live
  // etc.), the new tab's Apollo queries write to the normalized cache for
  // floors/buildings. Apollo then pushes a new `data` reference to
  // useSidebarSiteQuery — even though the actual site data hasn't changed.
  // That new reference would cascade through filteredItems → menuElement →
  // Ant Design Menu rebuild, resetting scroll position. By comparing a JSON
  // fingerprint we keep the downstream memos stable. The stringify only runs
  // when rawSites reference changes (via useMemo), not on every render.
  const rawSites = rawSiteData?.sites?.data;

  const sitesFingerprint = useMemo(() => JSON.stringify(rawSites ?? []), [rawSites]);
  const prevFingerprintRef = useRef(sitesFingerprint);
  const stableSitesRef = useRef(rawSites);

  // Intentional render-phase ref mutation: this is the "ref as previous value"
  // pattern (equivalent to usePrevious). Safe because the mutation is idempotent
  // — double-invocation in StrictMode produces the same result.
  if (sitesFingerprint !== prevFingerprintRef.current) {
    prevFingerprintRef.current = sitesFingerprint;
    stableSitesRef.current = rawSites;
  }
  const [filterValue, setFilterValue] = React.useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const { assignments, isUnrestricted, hasAccess } = useSpaceAccess();
  const [isHideMode, setIsHideMode] = useState(false);
  const [preHideOpenKeys, setPreHideOpenKeys] = useState<string[]>([]);
  const { hiddenIds, toggleHidden, setManyHidden, isHidden } = useHiddenSpaces();
  const filteredItemsRef = useRef<MenuItem[] | undefined>(undefined);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollTopRef = useRef(0);
  // Track scroll position so we can restore it after re-renders
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      scrollTopRef.current = el.scrollTop;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Restore scroll position synchronously before paint so the user never
  // sees a flash at the wrong position if a re-render slips through.
  // No dependency array is intentional — must run after every render since
  // any render could reset the scroll container. The body is cheap (one
  // scrollTop assignment) and bails early when position is 0.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || scrollTopRef.current === 0) return;
    el.scrollTop = scrollTopRef.current;
  });

  const onToggleSidebar = () => {
    onSidebarOpenClose(true);
  };
  useEffect(() => {
    const params = new URLSearchParams(search);
    const floorId = params.get('spaceId');
    const roomId = params.get('roomId');
    const zoneId = params.get('zoneId');
    const buildingId = params.get('buildingId');
    if (buildingId) {
      setSelectedKey(buildingId);
    } else if (roomId) {
      setSelectedKey(roomId);
    } else if (zoneId) {
      setSelectedKey(zoneId);
    } else if (floorId) {
      setSelectedKey(floorId);
    } else {
      setSelectedKey(null);
    }
  }, [search]);
  const { value: buildingMetricsEnabled } = useFeatureGate('building_level_metrics');
  const computedItems: MenuItem[] | undefined = useMemo(() => {
    // Use hasAccess from SpaceAccessContext which supports hierarchical access
    // If user has access to a building, they have access to all floors/rooms/zones in it
    const isZoneAccessible = (zoneId: string) => hasAccess('zone', zoneId);
    const isRoomAccessible = (roomId: string) => hasAccess('room', roomId);
    const isFloorAccessible = (floorId: string) => hasAccess('floor', floorId);
    const isBuildingAccessible = (buildingId: string) => hasAccess('building', buildingId);

    // For parent containers (floors/buildings/sites), we need to show them if ANY child is accessible
    // This is different from hasAccess which checks parent chain
    // Here we check if any descendant is accessible (for sidebar visibility)
    const isFloorOrChildrenAccessible = (
      floorId: string,
      rooms: readonly { id: string }[] | null | undefined,
      zones: readonly { id: string }[] | null | undefined,
    ) => {
      // First check if user has direct access to floor (which grants access to all children)
      if (isFloorAccessible(floorId)) return true;
      // Then check if any room or zone is accessible
      return (
        (rooms || []).some((r) => isRoomAccessible(r.id)) ||
        (zones || []).some((z) => isZoneAccessible(z.id))
      );
    };

    // For parent containers (sites/buildings), we need to show them if ANY child is accessible
    // This is different from hasAccess which checks parent chain
    // Here we check if any descendant is accessible (for sidebar visibility)
    const isBuildingOrChildrenAccessible = (
      buildingId: string,
      floors:
        | readonly {
            id: string;
            rooms?: readonly { id: string }[] | null;
            zones?: readonly { id: string }[] | null;
          }[]
        | null
        | undefined,
    ) => {
      // First check if user has direct access to building (which grants access to all children)
      if (isBuildingAccessible(buildingId)) return true;
      // Then check if any floor is accessible
      return (floors || []).some(
        (f) =>
          isFloorAccessible(f.id) ||
          (f.rooms || []).some((r) => isRoomAccessible(r.id)) ||
          (f.zones || []).some((z) => isZoneAccessible(z.id)),
      );
    };

    const isSiteOrChildrenAccessible = (
      siteId: string,
      buildings:
        | readonly {
            id: string;
            floors?:
              | readonly {
                  id: string;
                  rooms?: readonly { id: string }[] | null;
                  zones?: readonly { id: string }[] | null;
                }[]
              | null;
          }[]
        | null
        | undefined,
    ) => {
      // First check if user has direct access to site
      if (hasAccess('site', siteId)) return true;
      // Then check if any building or its children are accessible
      return (buildings || []).some((b) => isBuildingOrChildrenAccessible(b.id, b.floors));
    };

    const allSites = stableSitesRef.current || [];
    const filteredSites = allSites.filter((site) =>
      isSiteOrChildrenAccessible(site.id, site.buildings || []),
    );

    return (
      filteredSites
        .map((site) => ({
          ...site,
          buildings: site?.buildings
            ?.filter((building) =>
              isBuildingOrChildrenAccessible(building.id, building.floors || []),
            )
            ?.map((building) => ({
              ...building,
              floors: building?.floors
                ?.filter((floor) =>
                  isFloorOrChildrenAccessible(floor.id, floor.rooms || [], floor.zones || []),
                )
                // In normal mode, filter out hidden floors
                ?.filter((floor) => isHideMode || !isHidden(floor.id))
                ?.map((floor) => {
                  // Combine rooms and zones into a single array
                  const combined = [
                    ...(floor?.rooms
                      ?.filter((room) => isRoomAccessible(room.id))
                      ?.map((room) => {
                        // Add zones to the room object if they have a matching roomId
                        const zonesForRoom =
                          floor?.zones
                            ?.filter((zone) => zone.roomId === room.id)
                            ?.filter((zone) => isZoneAccessible(zone.id)) ?? [];
                        return {
                          ...room,
                          zones: zonesForRoom,
                        };
                      })
                      .filter(
                        (room) =>
                          room.name?.toLowerCase().includes(filterValue.toLowerCase()) ||
                          room.zones.some(
                            (zone) => zone.name?.toLowerCase().includes(filterValue.toLowerCase()),
                          ),
                      ) ?? []),
                    ...(floor?.zones
                      ?.filter((zone) => !zone.roomId || !isRoomAccessible(zone.roomId))
                      .filter((zone) => isZoneAccessible(zone.id))
                      .filter(
                        (zone) => zone.name?.toLowerCase().includes(filterValue.toLowerCase()),
                      ) ?? []),
                  ];
                  combined.sort(naturalSort);
                  return {
                    ...floor,
                    roomszones: combined,
                  };
                })
                .filter(
                  (floor) =>
                    floor.name?.toLowerCase().includes(filterValue.toLowerCase()) ||
                    (floor.roomszones && floor.roomszones.length > 0),
                )
                ?.sort(naturalSort),
            }))
            .filter(
              (building) =>
                building?.name?.toLowerCase().includes(filterValue.toLowerCase()) ||
                building?.floors?.length > 0,
            )
            // In normal mode, filter out hidden buildings
            ?.filter((building) => isHideMode || !isHidden(building.id))
            ?.sort(naturalSort),
        }))
        .filter(
          (site) =>
            site?.name?.toLowerCase().includes(filterValue.toLowerCase()) ||
            site?.buildings?.length > 0,
        )
        // In normal mode, filter out hidden sites
        .filter((site) => isHideMode || !isHidden(site.id))
        ?.sort(naturalSort)
        .map((site) => ({
          label: (
            <span
              className="text-xs min-w-0 overflow-hidden text-ellipsis text-content-tertiary font-semibold leading-5 flex justify-between items-center"
              data-id="sidebar-nav-site-item"
            >
              {isHideMode && (
                <HideCheckbox
                  checked={isHidden(site.id)}
                  onChange={() => {
                    const willHide = !isHidden(site.id);
                    setManyHidden(collectDescendantIds(site), willHide);
                  }}
                />
              )}
              <TextMatchDisplay
                text={`Site - ${site.name}`}
                match={filterValue}
                variant="highlight"
              ></TextMatchDisplay>
              {!isHideMode && (
                <SiteEditMenu
                  id={site.id}
                  blockDelete={site.buildings && site.buildings.length > 0}
                />
              )}
            </span>
          ),
          type: 'group',
          key: site.id,
          children: site.buildings?.map((building) => ({
            label: (
              <div
                className={`flex w-full items-center pl-4 overflow-hidden text-ellipsis hover:rounded-r-full${
                  buildingMetricsEnabled && !isBuildingAccessible(building.id)
                    ? ' cursor-default opacity-80'
                    : ''
                }`}
                onClick={() => {
                  if (isHideMode) return;
                  if (buildingMetricsEnabled && isBuildingAccessible(building.id)) {
                    if (pathname.includes('/studio') || pathname.includes('/live')) {
                      navigate('/');
                    }

                    updateUrlParams(navigate, { buildingId: building.id });
                  }
                }}
              >
                {isHideMode && (
                  <HideCheckbox
                    checked={isHidden(building.id)}
                    onChange={() => {
                      const willHide = !isHidden(building.id);
                      setManyHidden(collectDescendantIds(building), willHide);
                    }}
                  />
                )}
                <div className="flex justify-between min-w-0 flex-1 mr-2">
                  <TextMatchDisplay
                    variant="highlight"
                    text={building.name}
                    match={filterValue}
                    className="overflow-hidden text-ellipsis whitespace-nowrap"
                  />
                  {!isHideMode && (
                    <BuildingEditMenu
                      id={building.id}
                      blockDelete={building.floors && building.floors.length > 0}
                    />
                  )}
                </div>
              </div>
            ),
            key: building.id,
            children: building.floors.map((floor) => ({
              label: (
                <div
                  className={`flex items-center pl-8 overflow-hidden text-ellipsis hover:rounded-r-full${
                    !isFloorAccessible(floor.id) ? ' cursor-default opacity-80' : ''
                  }`}
                  onClick={() => {
                    if (isHideMode) return;
                    if (!isFloorAccessible(floor.id)) return;
                    updateUrlParams(navigate, { floorId: floor.id });
                  }}
                >
                  {isHideMode && (
                    <HideCheckbox
                      checked={isHidden(floor.id)}
                      onChange={() => toggleHidden(floor.id)}
                    />
                  )}
                  <Icon name="layer" className="mr-2 text-content-primary" />
                  <TextMatchDisplay
                    variant="highlight"
                    text={floor.name}
                    match={filterValue}
                    className="overflow-hidden text-ellipsis whitespace-nowrap mr-4"
                  />
                </div>
              ),
              key: floor.id,
              children: [
                ...(floor?.roomszones?.map((item) => ({
                  label: (
                    <div
                      className="flex items-center pl-12 overflow-hidden whitespace-nowrap text-ellipsis"
                      onClick={() => {
                        if (isHideMode) return;
                        getTypeFromId(item.id) === 'room'
                          ? updateUrlParams(navigate, {
                              floorId: item.floorId,
                              roomId: item.id,
                            })
                          : updateUrlParams(navigate, {
                              floorId: item.floorId,
                              zoneId: item.id,
                            });
                      }}
                    >
                      <Icon
                        name={getTypeFromId(item.id) === 'room' ? 'room' : 'zone'}
                        className="mr-2"
                      />
                      <TextMatchDisplay
                        variant="highlight"
                        text={item.name}
                        match={filterValue}
                        className="overflow-hidden text-ellipsis whitespace-nowrap mr-4"
                      />{' '}
                    </div>
                  ),
                  key: item.id,
                  //zones within rooms (use item.zones from the pre-filtered data)
                  ...('zones' in item &&
                    (item as any).zones?.length > 0 && {
                      children: ((item as any).zones as NonNullable<typeof floor.zones>)
                        .sort(naturalSort)
                        .filter(
                          (zone) => zone.name?.toLowerCase().includes(filterValue.toLowerCase()),
                        )
                        .map((zone) => ({
                          label: (
                            <div className="flex items-center pl-[64px]">
                              <Icon name="zone" className="mr-2" />
                              <TextMatchDisplay
                                variant="highlight"
                                text={zone.name}
                                match={filterValue}
                                className="overflow-hidden text-ellipsis whitespace-nowrap mr-4"
                              />
                            </div>
                          ),
                          key: zone.id,
                          expandicon: <></>,
                          onClick: () => {
                            if (isHideMode) return;
                            updateUrlParams(navigate, {
                              floorId: zone.floorId,
                              zoneId: zone.id,
                            });
                          },
                        })),
                    }),
                })) || []),
              ],
            })),
          })),
        }))
    );

    // sitesFingerprint is a JSON string that only changes when site data content
    // changes — not when Apollo pushes a new reference for identical data.
    // stableSitesRef is a stable ref — not included in deps. navigate is
    // referentially stable in v7 data router mode, so including it is cheap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sitesFingerprint,
    filterValue,
    hasAccess,
    isUnrestricted,
    assignments,
    isHideMode,
    hiddenIds,
    buildingMetricsEnabled,
    navigate,
    pathname,
  ]);

  // Stabilize the filteredItems reference. Multiple Apollo cache subscribers
  // (SpaceAccessContext hierarchy query, SidebarSiteQuery) can push new
  // references for hasAccess/assignments/siteData when tab-level queries
  // write to the normalized cache — even though the menu structure hasn't
  // changed. By comparing a serialized key-tree string we return the previous
  // items reference, keeping menuElement stable and avoiding a full Ant
  // Design Menu rebuild (which would reset scroll position).
  const prevItemsKeysRef = useRef('');
  const stableItemsRef = useRef(computedItems);

  const itemsKeys = useMemo(() => {
    if (!computedItems) return '';
    const collect = (items: MenuItem[]): string =>
      items.map((i) => i.key + (i.children ? '(' + collect(i.children) + ')' : '')).join(',');
    return collect(computedItems);
  }, [computedItems]);

  // Intentional render-phase ref mutation (same idempotent pattern as above).
  if (itemsKeys !== prevItemsKeysRef.current) {
    prevItemsKeysRef.current = itemsKeys;
    stableItemsRef.current = computedItems;
  }

  const filteredItems = stableItemsRef.current;

  // Keep ref in sync so effects can read current items without depending on them
  filteredItemsRef.current = filteredItems;

  // Ensure ancestors of the selected item are expanded so it's visible.
  // Uses functional updater: if the needed keys are already open, returns the
  // same reference so React skips the re-render entirely. Never collapses
  // sections the user manually opened — intentionally preserves expanded
  // state when selectedKey becomes null (e.g. navigating to a page without
  // a spaceId).
  // itemsKeys is included so this re-runs when data loads after mount
  // (e.g. URL already has ?spaceId but items were undefined on first run).
  useEffect(() => {
    const items = filteredItemsRef.current;
    if (!items || isHideMode) return;
    if (!selectedKey) return;

    const findAncestorKeys = (menuItems: MenuItem[]): string[] => {
      const keys: string[] = [];
      menuItems.forEach((item) => {
        if (item.key === selectedKey) {
          keys.push(item.key);
        }
        if (item.children) {
          const childKeys = findAncestorKeys(item.children);
          if (childKeys.length > 0) {
            keys.push(item.key);
            keys.push(...childKeys);
          }
        }
      });
      return keys;
    };

    const neededKeys = findAncestorKeys(items);
    setOpenKeys((prev) => {
      const allPresent = neededKeys.every((k) => prev.includes(k));
      if (allPresent) return prev; // already visible — no update, no re-render
      return [...new Set([...prev, ...neededKeys])];
    });
  }, [selectedKey, isHideMode, itemsKeys]);

  // When entering hide mode, expand sites and buildings so every checkbox is visible
  // Floors don't need to expand since rooms/zones aren't hideable
  useEffect(() => {
    if (!isHideMode) return;
    const items = filteredItemsRef.current;
    if (!items) return;
    const allKeys: string[] = [];
    items.forEach((site) => {
      if (site.children?.length) {
        allKeys.push(site.key);
        site.children.forEach((building) => {
          if (building.children?.length) {
            allKeys.push(building.key);
          }
        });
      }
    });
    setOpenKeys(allKeys);
  }, [isHideMode]);

  const selectedKeys = useMemo(() => (selectedKey ? [selectedKey] : []), [selectedKey]);

  const handleOpenChange = useCallback(
    (oks: string[]) => {
      if (isHideMode) return;
      setOpenKeys(oks);
    },
    [isHideMode],
  );

  // Memoize the Menu element so context-triggered re-renders of SidebarMenu
  // don't cause Ant Design to re-render/re-mount the menu tree.
  const menuElement = useMemo(
    () => (
      <Menu
        data-id="sidebar-menu"
        inlineIndent={0}
        mode="inline"
        items={filteredItems}
        selectedKeys={selectedKeys}
        style={MENU_STYLE}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
      />
    ),
    [filteredItems, selectedKeys, openKeys, handleOpenChange],
  );

  return (
    <>
      <div className="flex gap-2 px-3 py-2 items-center">
        <MenuFoldOutIcon
          onClick={onToggleSidebar}
          className="w-4 h-4 shrink-0 m-2 text-content-primary cursor-pointer"
          color="currentColor"
          data-id="sidebar-toggle"
        />
        <SearchInput
          data-id="search-input"
          value={filterValue}
          onFocus={() => {
            datadogRum.addAction('Activate new sidebar search', { source: 'sites-and-buildings' });
          }}
          onClear={() => {
            setFilterValue('');
          }}
          onChange={(ev) => {
            setFilterValue(ev.target.value);
          }}
        />{' '}
        {!sidebarOpen && <CreateDropdownButton variant="compact" />}
      </div>

      {siteError ? (
        <div className="px-4 py-3 text-xs text-content-secondary">
          Failed to load spaces. Please try refreshing.
        </div>
      ) : filterValue && filteredItems?.length === 0 ? (
        <NullSearch />
      ) : (
        <div className="flex flex-col min-h-0">
          <div
            ref={scrollRef}
            className="flex-grow overflow-auto"
            style={{ height: 'calc(100vh - 217px)' }}
          >
            {menuElement}
          </div>
          {sidebarOpen && (
            <div className="">
              <div>
                <hr className="border-t border-default m-4 mb-0" />
                <div className="flex justify-end gap-2 m-4">
                  <button
                    className="whitespace-nowrap rounded flex items-center text-sm justify-center text-content-primary hover:bg-surface-hover border border-default py-2 px-4"
                    onClick={() => {
                      if (!isHideMode) {
                        setPreHideOpenKeys(openKeys);
                      } else {
                        setOpenKeys(preHideOpenKeys);
                      }
                      setIsHideMode(!isHideMode);
                    }}
                    data-id="hide-toggle-button"
                  >
                    <Icon
                      name={isHideMode ? 'view' : 'view-slash'}
                      className="w-4 mr-2"
                      color="currentColor"
                    />
                    {isHideMode ? 'Done' : 'Hide'}
                    {!isHideMode && hiddenIds.size > 0 && (
                      <span className="ml-1.5 bg-content-primary text-white dark:text-black text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                        {hiddenIds.size}
                      </span>
                    )}
                  </button>
                  <div className={isHideMode ? 'opacity-50 pointer-events-none' : ''}>
                    <CreateDropdownButton />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default React.memo(SidebarMenu);

export function NullSearch() {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center items-center">
      <div className="flex flex-col items-center gap-2 px-3 py-2 w-[216px]">
        <span className="text-xs font-semibold">{t('noResults')}</span>
        <span className="text-[10px] text-center">{t('searchAction')}</span>
      </div>
    </div>
  );
}

export const SidebarMenuZoneFragment = gql`
  fragment SidebarMenuZone on Zone {
    id: zone_id
    name
    floorId: floor_id
    roomId: room_id
  }
`;

export const SidebarMenuRoomFragment = gql`
  fragment SidebarMenuRoom on Room {
    id: room_id
    name
    floorId: floor_id
  }
`;
export const SidebarMenuFloorFragment = gql`
  fragment SidebarMenuFloor on Floor {
    id: floor_id
    name
    rooms {
      id: room_id
      ...SidebarMenuRoom
    }
    zones {
      id: zone_id
      ...SidebarMenuZone
    }
  }
  ${SidebarMenuRoomFragment}
  ${SidebarMenuZoneFragment}
`;

export const SidebarMenuBuildingFragment = gql`
  fragment SidebarMenuBuilding on Building {
    id
    name
    floors {
      id: floor_id
      capacity {
        max
      }
      ...SidebarMenuFloor
    }
  }
  ${SidebarMenuFloorFragment}
`;

export const SidebarMenuSiteFragment = gql`
  fragment SidebarMenuSite on Site {
    id
    name
    buildings {
      id
      ...SidebarMenuBuilding
    }
  }
  ${SidebarMenuBuildingFragment}
`;

export const SidebarMenuFloorQuery = gql`
  query SidebarFloor {
    floors {
      data {
        id: floor_id
        ...SidebarMenuFloor
      }
    }
  }
  ${SidebarMenuFloorFragment}
`;

export const SidebarMenuSiteQuery = gql`
  query SidebarSite {
    sites {
      data {
        id
        ...SidebarMenuSite
      }
    }
  }
  ${SidebarMenuSiteFragment}
`;
